import React, { useEffect, useState } from 'react';
import { DoctDetail, DoctorName, ViewBox, ViewBox_1, ViewInner, ViewInner_Box1, ViewInner_Box2, ViewBox_3, ViewBox_2 } from '../Style';
import mycliniqLogo from '../../../../../assets/images/logo.svg';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import html2pdf from "html2pdf.js";
import { useSelector, useDispatch } from 'react-redux';
import { getMedicalRecordsByIdRequest, getDoctorProfileByIdRequest, getClinicProfileByIdRequest, getPatientByIdRequest, getPatientInfoByIdRequest } from "src/modules/app/store/appActions";
import dayjs from 'dayjs';
import { getUserFromToken } from 'src/DecodeToken';
import { ReactComponent as CallIcon } from '../../../../../assets/images/call.svg'
import storage from 'src/utils/storageUtils';
import { Box } from '@mui/material';

const ViewPrescription = ({ prescriptionId, onClose, mode, onActionComplete }) => {
    const id = prescriptionId;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const shouldPrint = mode === 'print';
    const shouldDownload = mode === 'download';

    const [viewPrescription, setViewPrescription] = useState(null);
    const [doctorProfile, setDoctorProfile] = useState(null);
    const [clinicProfile, setClinicProfile] = useState(null);
    const [hasPrinted, setHasPrinted] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const [patientDetails, setPatientDetails] = useState(null);
    const [patientInfo, setPatientInfo] = useState(null);

    const clinicData = useSelector((state) => state.app.clinics);
    const clinicId = clinicData?.clinicId;

    const doctId = getUserFromToken();

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [prescriptionRes, doctorRes, clinicRes] = await Promise.all([
                    new Promise((resolve, reject) => dispatch(getMedicalRecordsByIdRequest(id, resolve, reject))),
                    new Promise((resolve, reject) => dispatch(getDoctorProfileByIdRequest(doctId, resolve, reject))),
                    new Promise((resolve, reject) => dispatch(getClinicProfileByIdRequest(clinicId, resolve, reject))),
                ]);

                if (prescriptionRes) setViewPrescription(prescriptionRes);
                if (doctorRes) setDoctorProfile(doctorRes);
                if (clinicRes) setClinicProfile(clinicRes);
            } catch (error) {
                console.log("Error fetching one of the items", error);
            }
        };

        if (id && doctId && clinicId) {
            fetchAllData();
        }
    }, [id, doctId, clinicId, dispatch]);

    useEffect(() => {
        const pid = viewPrescription?.patient?.id || viewPrescription?.patient || null;
        if (!pid) return;
        const fetchPatient = async () => {
            try {
                const [pRes, pInfoRes] = await Promise.all([
                    new Promise((resolve, reject) => dispatch(getPatientByIdRequest(pid, resolve, reject))),
                    new Promise((resolve, reject) => dispatch(getPatientInfoByIdRequest(pid, resolve, reject))),
                ]);
                const p = pRes?.payload?.data?.patient || pRes?.patient || pRes || null;
                const info = Array.isArray(pInfoRes?.payload?.data) ? pInfoRes.payload.data[0] : pInfoRes;
                if (p) setPatientDetails(p);
                if (info) setPatientInfo(info);
            } catch (err) {
                console.warn('Failed to fetch patient info:', err?.message || err);
            }
        };
        fetchPatient();
    }, [viewPrescription, dispatch]);

    useEffect(() => {
        if (shouldPrint && !hasPrinted && viewPrescription) {
            setHasPrinted(true);
            setTimeout(() => {
                window.print();
                setTimeout(() => {
                    if (onActionComplete) onActionComplete();
                }, 300);
            }, 300);
        }

        if (shouldDownload && !hasDownloaded && viewPrescription) {
            setHasDownloaded(true);
            setTimeout(() => {
                const content = document.getElementById("print-content");

                html2pdf()
                    .set({
                        margin: 0.5,
                        filename: `Prescription-${id}.pdf`,
                        image: { type: "jpeg", quality: 0.98 },
                        html2canvas: { scale: 2 },
                        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
                    })
                    .from(content)
                    .save()
                    .then(() => {
                        if (onActionComplete) onActionComplete();
                    });
            }, 500);
        }
    }, [shouldPrint, shouldDownload, viewPrescription, hasPrinted, hasDownloaded, id, onActionComplete]);

    return (
        <div id='print-content'>
            <ViewBox  >
                <ViewBox_1>
                    <ViewInner sx={{ alignItems: 'flex-start' }}>
                        <ViewInner_Box1>
                            <DoctorName sx={{ fontSize: 20, fontWeight: 700 }}>
                                {(clinicProfile?.clinicInfo?.clinicName || clinicData?.currentClinic?.clinicName)
                                    ? `${clinicProfile?.clinicInfo?.clinicName || clinicData?.currentClinic?.clinicName}`
                                    : ''}
                                {(clinicProfile?.clinicInfo?.clinicName || clinicData?.currentClinic?.clinicName) ? ' - ' : ''}
                                Dr. {(doctorProfile?.personalInfo?.fullName || storage.get('DOCTORNAME') || '')
                                    .split(' ')
                                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ')}
                            </DoctorName>
                            <DoctDetail sx={{ mt: 0.5 }}>
                                {(doctorProfile?.professionalInfo?.degree && 'QUALIFICATION') || ''}
                            </DoctDetail>
                            <DoctDetail>{doctorProfile?.professionalInfo?.degree || ''}</DoctDetail>
                            <DoctDetail>
                                Certification: {doctorProfile?.professionalInfo?.certificateNo || doctorProfile?.professionalInfo?.licenseNo || 'N/A'}
                            </DoctDetail>
                            {(clinicProfile?.clinicInfo?.address || clinicData?.currentClinic?.address) && (
                                <DoctDetail>{clinicProfile?.clinicInfo?.address || clinicData?.currentClinic?.address}</DoctDetail>
                            )}
                        </ViewInner_Box1>
                        <ViewInner_Box2 sx={{ alignItems: 'flex-end' }}>
                            <DoctDetail>Date: {dayjs().format('YYYY-MM-DD')}</DoctDetail>
                            <img src={mycliniqLogo} alt="MyCliniq" style={{ height: 36, marginTop: 8 }} />
                        </ViewInner_Box2>
                    </ViewInner>
                </ViewBox_1>


                <ViewBox_2>
                    <ViewInner>
                        <ViewInner_Box1 sx={{ width: '100%' }}>
                            <DoctDetail sx={{ mb: 1 }}>
                                Patient Name:
                                <span style={{ display: 'inline-block', minWidth: 320, marginLeft: 8, borderBottom: '1px solid #BDBDBD' }}>
                                    &nbsp;{patientDetails?.fullName || viewPrescription?.patient?.fullName || 'N/A'}&nbsp;
                                </span>
                            </DoctDetail>
                            <DoctDetail sx={{ mb: 1 }}>
                                Address:
                                <span style={{ display: 'inline-block', minWidth: 320, marginLeft: 8, borderBottom: '1px solid #BDBDBD' }}>
                                    &nbsp;{patientDetails?.area || patientInfo?.area || patientDetails?.customData?.address || 'N/A'}&nbsp;
                                </span>
                            </DoctDetail>
                            <DoctDetail sx={{ mb: 1 }}>
                                Age:
                                <span style={{ display: 'inline-block', minWidth: 140, marginLeft: 8, borderBottom: '1px solid #BDBDBD' }}>
                                    &nbsp;{(function() {
                                        const ageStr = patientDetails?.detailedAge?.ageString || patientInfo?.detailedAge?.ageString || viewPrescription?.patient?.detailedAge?.ageString;
                                        if (ageStr) return ageStr;
                                        const birth = patientDetails?.dateOfBirth || patientInfo?.dateOfBirth || viewPrescription?.patient?.dateOfBirth;
                                        if (birth) {
                                            const b = dayjs(birth);
                                            const now = dayjs();
                                            const years = now.diff(b, 'year');
                                            const months = now.diff(b.add(years, 'year'), 'month');
                                            const days = now.diff(b.add(years, 'year').add(months, 'month'), 'day');
                                            return `${years} ${years === 1 ? 'Year' : 'Years'} ${months} ${months === 1 ? 'Month' : 'Months'} ${days} ${days === 1 ? 'Day' : 'Days'}`;
                                        }
                                        const baseAge = patientDetails?.age || patientInfo?.age || viewPrescription?.patient?.age;
                                        return baseAge ? `${baseAge} ${baseAge === 1 ? 'Year' : 'Years'}` : 'N/A';
                                    })()}&nbsp;
                                </span>
                                &nbsp;&nbsp; Date:
                                <span style={{ display: 'inline-block', minWidth: 140, marginLeft: 8, borderBottom: '1px solid #BDBDBD' }}>
                                    &nbsp;{dayjs().format('YYYY-MM-DD')}&nbsp;
                                </span>
                            </DoctDetail>
                            <DoctDetail sx={{ mb: 2 }}>
                                Diagnosis:
                                <span style={{ display: 'inline-block', minWidth: 320, marginLeft: 8, borderBottom: '1px solid #BDBDBD' }}>
                                    &nbsp;{viewPrescription?.diagnosis || 'N/A'}&nbsp;
                                </span>
                            </DoctDetail>

                            <DoctorName sx={{ fontSize: 26, color: '#1E63E9', fontWeight: 700, mt: 2 }}>Rx</DoctorName>
                            {/* Rx space for manual writing */}
                            <Box sx={{ height: 200 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 8 }}>
                                <Box sx={{ width: 200, textAlign: 'center' }}>
                                    <Box sx={{ borderTop: '1px solid #9E9E9E', mb: 0.5 }} />
                                    <DoctDetail>SIGNATURE</DoctDetail>
                                </Box>
                            </Box>
                        </ViewInner_Box1>
                    </ViewInner>
                </ViewBox_2>


                <ViewBox_3>
                    <ViewInner>
                        <ViewInner_Box1 sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                            <Box>
                                <DoctDetail>HOSPITAL</DoctDetail>
                                <DoctDetail>Phone: {doctorProfile?.loginInfo?.contactNumber || 'N/A'}</DoctDetail>
                            </Box>
                            <Box>
                                <DoctDetail>Address: {clinicProfile?.clinicInfo?.address || 'N/A'}</DoctDetail>
                                <DoctDetail>Email: {doctorProfile?.loginInfo?.email || clinicProfile?.clinicInfo?.email || 'N/A'}</DoctDetail>
                            </Box>
                            <Box>
                                <DoctDetail>Web: {clinicProfile?.clinicInfo?.website || 'N/A'}</DoctDetail>
                            </Box>
                        </ViewInner_Box1>
                        <ViewInner_Box2>
                            <DoctDetail>Mon - Sat (10:00 am to 7:00 pm)</DoctDetail>
                        </ViewInner_Box2>
                    </ViewInner>

                </ViewBox_3>


            </ViewBox >
        </div>
    )
}

export default ViewPrescription
